import React from 'react'
import { ApolloProvider } from '@apollo/client'
import { RecoilRoot } from 'recoil'
import GlobalStyles from '@mui/material/GlobalStyles'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { CurrentUserContextProvider } from '../contexts/CurrentUserContext'
import theme from '../theme'
import { User, Tenant } from 'types'
import { graphqlClient } from 'graphqlClient'
import PdfReportNavbar from './PdfReport/Navbar'
import PdfReportProgress from './PdfReport/Progress'
import PdfReportPrint from './PdfReport/Print'
import { ChartOptionsContextProvider } from 'contexts/ChartOptionsContext'
import { ChartOptionElement } from 'gqlTypes'
import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'

type Props = {
  currentUser: User
  tenant: Tenant
  pdfReportId: string
  graphqlUri: string
  reportId: string
  reportTitle: string
  sectionId?: string
  favorites: boolean
  confidential: boolean
}

const PdfReportApp: React.FC<Props> = ({
  currentUser,
  tenant,
  pdfReportId,
  graphqlUri,
  reportId,
  reportTitle,
  sectionId,
  favorites,
  confidential,
}) => {
  // https://github.com/emotion-js/emotion/issues/1248#issuecomment-2495612844
  // make sure all css is appended to the style tag in text instead of the CSSOM
  const emotionCache = createCache({
    key: 'css',
    speedy: false, // <--- key setting
  })
  return (
    <CacheProvider value={emotionCache}>
      <RecoilRoot>
        <ApolloProvider client={graphqlClient(graphqlUri)}>
          <ThemeProvider theme={theme}>
            <CurrentUserContextProvider
              currentUser={currentUser}
              tenant={tenant}
            >
              <ChartOptionsContextProvider
                id={reportId || ''}
                type={ChartOptionElement.Report}
              >
                <CssBaseline />
                <GlobalStyles styles={{ body: { backgroundColor: '#fff' } }} />
                {sectionId ? (
                  <PdfReportPrint
                    sectionId={sectionId}
                    reportId={reportId}
                    favorites={favorites}
                    confidential={confidential}
                  />
                ) : (
                  <>
                    <PdfReportNavbar
                      currentUser={currentUser}
                      tenant={tenant}
                      reportId={reportId}
                      reportTitle={reportTitle}
                    />
                    <PdfReportProgress
                      pdfReportId={pdfReportId}
                      tenant={tenant}
                    />
                  </>
                )}
              </ChartOptionsContextProvider>
            </CurrentUserContextProvider>
          </ThemeProvider>
        </ApolloProvider>
      </RecoilRoot>
    </CacheProvider>
  )
}

export default PdfReportApp
